/**
 * InspirationDetailPage.jsx - Dettaglio articolo
 * ===============================================
 * Design: Layout boxed con sezioni flessibili
 * 100% CMS-driven - NO HARDCODED TEXT
 */

import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Clock, Calendar, Bookmark, Share2, Facebook, Linkedin,
  ChevronRight, Play, Plus, Loader2, Heart, ChevronDown, ZoomIn
} from 'lucide-react';
import supabase from '../lib/supabase';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ImageLightbox from '../components/ImageLightbox';

const InspirationDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  
  // State
  const [config, setConfig] = useState(null);
  const [post, setPost] = useState(null);
  const [sections, setSections] = useState([]);
  const [postMaterials, setPostMaterials] = useState([]);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [hotspots, setHotspots] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeHotspot, setActiveHotspot] = useState(null);
  const [activeTocItem, setActiveTocItem] = useState(null);
  
  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Load post data
  useEffect(() => {
    const loadPost = async () => {
      setIsLoading(true);
      try {
        // Load config
        const { data: configData } = await supabase
          .from('blueprint_inspiration_config')
          .select('*')
          .eq('lang', 'it')
          .single();
        if (configData) setConfig(configData);

        // Load post with relations
        const { data: postData, error } = await supabase
          .from('blueprint_posts')
          .select(`
            *,
            category:blueprint_post_categories(slug, name_it, color),
            author:blueprint_authors(name, slug, avatar_url, role, bio_short)
          `)
          .eq('slug', slug)
          .eq('status', 'published')
          .single();

        if (error || !postData) {
          navigate('/inspiration/articles');
          return;
        }
        
        setPost(postData);

        // Increment view count
        try {
          await supabase.rpc('increment_post_views', { post_id: postData.id });
        } catch (e) {}

        // Load sections
        const { data: sectionsData } = await supabase
          .from('blueprint_post_sections')
          .select('*')
          .eq('post_id', postData.id)
          .order('order_index');
        if (sectionsData) setSections(sectionsData);

        // Load section hotspots
        if (sectionsData?.length > 0) {
          const sectionIds = sectionsData.map(s => s.id);
          const { data: sectionHotspots } = await supabase
            .from('blueprint_section_hotspots')
            .select(`*, material:materials(id, name, image_url, category)`)
            .in('section_id', sectionIds);
          
          // Attach hotspots to sections
          if (sectionHotspots) {
            const hotspotsMap = {};
            sectionHotspots.forEach(h => {
              if (!hotspotsMap[h.section_id]) hotspotsMap[h.section_id] = [];
              hotspotsMap[h.section_id].push(h);
            });
            setSections(prev => prev.map(s => ({
              ...s,
              hotspots: hotspotsMap[s.id] || []
            })));
          }
        }

        // Load cover hotspots
        const { data: coverHotspots } = await supabase
          .from('blueprint_post_hotspots')
          .select(`*, material:materials(id, name, image_url, category)`)
          .eq('post_id', postData.id);
        if (coverHotspots) setHotspots(coverHotspots);

        // Load post materials
        const { data: materialsData } = await supabase
          .from('blueprint_post_materials')
          .select(`*, material:materials(id, name, image_url, category, origin)`)
          .eq('post_id', postData.id)
          .order('order_index');
        if (materialsData) setPostMaterials(materialsData.map(pm => pm.material).filter(Boolean));

        // Load gallery
        const { data: galleryData } = await supabase
          .from('blueprint_post_gallery')
          .select(`*`)
          .eq('post_id', postData.id)
          .eq('is_visible', true)
          .order('order_index');
        
        if (galleryData && galleryData.length > 0) {
          // Load gallery hotspots
          const galleryIds = galleryData.map(g => g.id);
          const { data: galleryHotspots } = await supabase
            .from('blueprint_gallery_hotspots')
            .select(`*, material:materials(id, name, image_url, category)`)
            .in('gallery_image_id', galleryIds);
          
          const hotspotsMap = {};
          if (galleryHotspots) {
            galleryHotspots.forEach(h => {
              if (!hotspotsMap[h.gallery_image_id]) hotspotsMap[h.gallery_image_id] = [];
              hotspotsMap[h.gallery_image_id].push(h);
            });
          }
          
          setGallery(galleryData.map(g => ({
            ...g,
            hotspots: hotspotsMap[g.id] || []
          })));
        }

        // Load related posts
        const { data: relatedData } = await supabase
          .from('blueprint_posts')
          .select(`
            id, slug, title_it, cover_image_url, reading_time, published_at,
            category:blueprint_post_categories(name_it, color)
          `)
          .eq('status', 'published')
          .eq('category_id', postData.category_id)
          .neq('id', postData.id)
          .limit(3);
        if (relatedData) setRelatedPosts(relatedData);
      } catch (err) {
        console.error('Error loading post:', err);
        navigate('/inspiration/articles');
      } finally {
        setIsLoading(false);
      }
    };

    if (slug) loadPost();
  }, [slug, navigate]);

  // Text helper
  const t = (key, fallback) => config?.[key] || fallback;

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  // Build TOC from sections
  const tableOfContents = sections
    .filter(s => s.title_it && s.is_visible !== false)
    .map(s => ({
      id: s.anchor_id || `section-${s.order_index}`,
      title: s.title_it
    }));

  // Scroll to section
  const scrollToSection = (anchorId) => {
    const el = document.getElementById(anchorId);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveTocItem(anchorId);
    }
  };

  // Share
  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareTitle = post?.title_it || '';

  // Open lightbox
  const openLightbox = (images, index = 0) => {
    setLightboxImages(images);
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  // Build all article images for lightbox
  const getAllImages = () => {
    const images = [];
    
    // Cover image
    if (post?.cover_image_url) {
      images.push({
        url: post.cover_image_url,
        alt: post.title_it,
        hotspots: hotspots,
        caption: post.title_it
      });
    }
    
    // Section images
    sections.forEach(section => {
      if (section.image_url && section.image_position !== 'none') {
        images.push({
          url: section.image_url,
          alt: section.title_it,
          hotspots: section.hotspots || [],
          caption: section.title_it
        });
      }
    });
    
    // Gallery images
    gallery.forEach(img => {
      images.push({
        url: img.image_url,
        alt: img.alt_text,
        hotspots: img.hotspots || [],
        caption: img.caption_it
      });
    });
    
    return images;
  };

  // Render hotspot
  const renderHotspot = (hotspot, key) => (
    <button
      key={key}
      className="absolute w-8 h-8 -translate-x-1/2 -translate-y-1/2 z-10 group"
      style={{ left: `${hotspot.position_x}%`, top: `${hotspot.position_y}%` }}
      onClick={() => setActiveHotspot(activeHotspot === hotspot.id ? null : hotspot.id)}
    >
      <div className="absolute inset-0 rounded-full border-2 border-[#B08B5C]/40 animate-ping opacity-75" />
      <div className="relative w-8 h-8 rounded-full border-2 border-[#B08B5C] bg-black/40 backdrop-blur-sm flex items-center justify-center hover:bg-[#B08B5C] hover:border-[#B08B5C] transition-colors">
        <Plus className="w-4 h-4 text-[#B08B5C] group-hover:text-black" />
      </div>
      
      {activeHotspot === hotspot.id && hotspot.material && (
        <div className="absolute left-10 top-0 w-52 bg-[#121212] border border-white/10 rounded-xl p-3 z-30 shadow-2xl">
          <Link 
            to={`/materials/${hotspot.material.id}`}
            className="flex gap-3 hover:opacity-80 transition-opacity"
          >
            {hotspot.material.image_url && (
              <img 
                src={hotspot.material.image_url} 
                alt={hotspot.material.name}
                className="w-12 h-12 rounded-lg object-cover"
              />
            )}
            <div className="flex-1 min-w-0">
              <p className="text-white font-medium text-sm truncate">{hotspot.material.name}</p>
              <p className="text-white/40 text-xs capitalize">{hotspot.material.category}</p>
              <span className="text-[#B08B5C] text-xs mt-1 inline-block">Scopri →</span>
            </div>
          </Link>
        </div>
      )}
    </button>
  );

  // Render section image with position - Clickable
  const renderSectionImage = (section, sectionIndex) => {
    if (!section.image_url || section.image_position === 'none') return null;

    const imageElement = (
      <div 
        className="relative cursor-zoom-in group"
        onClick={() => openLightbox([{
          url: section.image_url,
          alt: section.title_it,
          hotspots: section.hotspots || [],
          caption: section.title_it
        }], 0)}
      >
        <img 
          src={section.image_url} 
          alt={section.title_it || ''}
          className={`rounded-xl object-cover transition-transform duration-500 group-hover:scale-[1.02] ${
            section.image_position === 'full-width' ? 'w-full' : 'w-full h-auto'
          }`}
        />
        
        {/* Zoom indicator */}
        <div className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center text-white/60 opacity-0 group-hover:opacity-100 transition-opacity">
          <ZoomIn size={16} />
        </div>
        
        {/* Hotspots */}
        {section.hotspots?.map((h, i) => (
          <div key={`section-${section.id}-hotspot-${i}`} onClick={(e) => e.stopPropagation()}>
            {renderHotspot(h, `section-${section.id}-hotspot-${i}`)}
          </div>
        ))}
      </div>
    );

    return imageElement;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-[#B08B5C]" />
      </div>
    );
  }

  if (!post) return null;

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex flex-col">
      <Header />
      
      <main className="flex-1 pt-20">
        {/* Boxed Container */}
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          
          {/* Back Link */}
          <div className="py-6">
            <Link 
              to="/inspiration/articles" 
              className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              {t('back_to_articles', 'Torna a tutti gli articoli')}
            </Link>
          </div>

          {/* Main Layout: Content + Sidebar */}
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
            
            {/* Main Content */}
            <article className="flex-1 min-w-0">
              
              {/* Hero Section - Boxed */}
              <section className="mb-10">
                {/* Cover Image - Clickable */}
                <div 
                  className="relative rounded-2xl overflow-hidden mb-8 cursor-zoom-in group"
                  onClick={() => openLightbox([{
                    url: post.cover_image_url,
                    alt: post.title_it,
                    hotspots: hotspots,
                    caption: post.title_it
                  }], 0)}
                >
                  {post.cover_image_url && (
                    <img 
                      src={post.cover_image_url} 
                      alt={post.title_it || post.title}
                      className="w-full aspect-[16/9] object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  )}
                  
                  {/* Zoom indicator */}
                  <div className="absolute top-4 right-4 w-10 h-10 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center text-white/60 opacity-0 group-hover:opacity-100 transition-opacity">
                    <ZoomIn size={18} />
                  </div>
                  
                  {/* Hotspots on cover */}
                  {hotspots.map((h, i) => (
                    <div key={`cover-hotspot-${i}`} onClick={(e) => e.stopPropagation()}>
                      {renderHotspot(h, `cover-hotspot-${i}`)}
                    </div>
                  ))}
                </div>

                {/* Category */}
                {post.category && (
                  <Link 
                    to={`/inspiration/articles?category=${post.category.slug}`}
                    className="inline-block text-[11px] font-bold tracking-[0.25em] uppercase mb-4 hover:opacity-80"
                    style={{ color: post.category.color || '#B08B5C' }}
                  >
                    {post.category.name_it}
                  </Link>
                )}
                
                {/* Title */}
                <h1 className="text-3xl md:text-4xl lg:text-[44px] font-light text-white leading-[1.15] mb-4">
                  {post.title_it || post.title}
                </h1>
                
                {/* Subtitle */}
                {(post.subtitle_it || post.excerpt_it) && (
                  <p className="text-white/60 text-lg mb-6 max-w-2xl leading-relaxed">
                    {post.subtitle_it || post.excerpt_it}
                  </p>
                )}
                
                {/* Meta Row */}
                <div className="flex flex-wrap items-center gap-6 pb-8 border-b border-white/10">
                  {/* Author */}
                  {post.author && (
                    <div className="flex items-center gap-3">
                      {post.author.avatar_url ? (
                        <img 
                          src={post.author.avatar_url} 
                          alt={post.author.name}
                          className="w-11 h-11 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-11 h-11 rounded-full bg-[#2a2a2a] flex items-center justify-center text-white/40">
                          {post.author.name?.charAt(0)}
                        </div>
                      )}
                      <div>
                        <p className="text-white text-sm font-medium">{post.author.name}</p>
                        <p className="text-white/50 text-xs">{post.author.role}</p>
                      </div>
                    </div>
                  )}
                  
                  <div className="flex items-center gap-4 text-white/40 text-sm">
                    <span className="flex items-center gap-1.5">
                      <Clock className="w-4 h-4" />
                      {post.reading_time || 5} min
                    </span>
                    <span>{formatDate(post.published_at)}</span>
                  </div>
                </div>
              </section>

              {/* Article Sections */}
              <div className="space-y-12">
                {sections.length > 0 ? (
                  sections.filter(s => s.is_visible !== false).map((section, idx) => (
                    <section 
                      key={section.id} 
                      id={section.anchor_id || `section-${idx}`}
                      className="scroll-mt-24"
                    >
                      {/* Section Title */}
                      {section.title_it && (
                        <h2 className="text-2xl md:text-3xl font-light text-white mb-6">
                          {section.title_it}
                        </h2>
                      )}

                      {/* Image Above */}
                      {section.image_position === 'above' && (
                        <div className="mb-6">
                          {renderSectionImage(section)}
                        </div>
                      )}

                      {/* Full Width Image */}
                      {section.image_position === 'full-width' && (
                        <div className="mb-6 -mx-6 md:-mx-12 lg:-mx-0">
                          {renderSectionImage(section)}
                        </div>
                      )}

                      {/* Boxed Image */}
                      {section.image_position === 'boxed' && (
                        <div className="mb-6 max-w-2xl mx-auto">
                          {renderSectionImage(section)}
                        </div>
                      )}

                      {/* Left/Right Layout */}
                      {['left', 'right'].includes(section.image_position) ? (
                        <div className={`flex flex-col ${section.image_position === 'left' ? 'md:flex-row' : 'md:flex-row-reverse'} gap-6 md:gap-8`}>
                          <div style={{ flex: `0 0 ${section.image_width || 50}%` }}>
                            {renderSectionImage(section)}
                          </div>
                          <div className="flex-1">
                            <div 
                              className="prose prose-invert prose-lg max-w-none
                                prose-headings:font-light prose-headings:text-white
                                prose-p:text-white/70 prose-p:leading-relaxed
                                prose-a:text-[#B08B5C] prose-a:no-underline hover:prose-a:underline
                                prose-strong:text-white prose-strong:font-medium
                                prose-blockquote:border-l-[#B08B5C] prose-blockquote:text-white/60
                                prose-img:rounded-xl"
                              dangerouslySetInnerHTML={{ __html: section.content_it || '' }}
                            />
                          </div>
                        </div>
                      ) : (
                        /* Default: Content only or with above/below/full/boxed image */
                        <div 
                          className="prose prose-invert prose-lg max-w-none
                            prose-headings:font-light prose-headings:text-white
                            prose-p:text-white/70 prose-p:leading-relaxed
                            prose-a:text-[#B08B5C] prose-a:no-underline hover:prose-a:underline
                            prose-strong:text-white prose-strong:font-medium
                            prose-blockquote:border-l-[#B08B5C] prose-blockquote:text-white/60
                            prose-img:rounded-xl"
                          dangerouslySetInnerHTML={{ __html: section.content_it || '' }}
                        />
                      )}

                      {/* Image Below */}
                      {section.image_position === 'below' && (
                        <div className="mt-6">
                          {renderSectionImage(section)}
                        </div>
                      )}

                      {/* Video Block */}
                      {section.video_url && (
                        <div className="mt-8 bg-[#121212] rounded-2xl overflow-hidden">
                          <div className="flex flex-col md:flex-row">
                            <div className="relative md:w-1/2 aspect-video md:aspect-auto">
                              <img 
                                src={section.video_thumbnail_url || post.cover_image_url}
                                alt={section.video_title || 'Video'}
                                className="w-full h-full object-cover"
                              />
                              <a
                                href={section.video_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="absolute inset-0 flex items-center justify-center group"
                              >
                                <div className="w-16 h-16 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center group-hover:bg-white/20 transition-colors">
                                  <Play className="w-7 h-7 text-white ml-1" />
                                </div>
                              </a>
                            </div>
                            <div className="md:w-1/2 p-6 md:p-8 flex flex-col justify-center">
                              <p className="text-[#B08B5C] text-xs uppercase tracking-wider mb-2">
                                {t('exclusive_interview', 'Intervista esclusiva')}
                              </p>
                              <h3 className="text-white text-xl font-medium mb-2">
                                {section.video_title || 'Guarda il video'}
                              </h3>
                              {section.video_description && (
                                <p className="text-white/60 text-sm mb-4">
                                  {section.video_description}
                                </p>
                              )}
                              <a
                                href={section.video_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 text-white/60 text-sm hover:text-white"
                              >
                                <Play className="w-4 h-4" />
                                Guarda {section.video_duration || ''}
                              </a>
                            </div>
                          </div>
                        </div>
                      )}
                    </section>
                  ))
                ) : (
                  /* Fallback: Use content_it if no sections */
                  <div 
                    className="prose prose-invert prose-lg max-w-none
                      prose-headings:font-light prose-headings:text-white
                      prose-p:text-white/70 prose-p:leading-relaxed
                      prose-a:text-[#B08B5C] prose-a:no-underline hover:prose-a:underline
                      prose-strong:text-white prose-strong:font-medium
                      prose-blockquote:border-l-[#B08B5C] prose-blockquote:text-white/60
                      prose-img:rounded-xl"
                    dangerouslySetInnerHTML={{ 
                      __html: post.content_it || post.content || '<p>Contenuto in arrivo...</p>' 
                    }}
                  />
                )}
              </div>

              {/* Author Box */}
              {post.author && (
                <div className="mt-12 pt-8 border-t border-white/10">
                  <div className="flex items-start gap-5 p-6 bg-[#121212] rounded-2xl">
                    {post.author.avatar_url && (
                      <img 
                        src={post.author.avatar_url}
                        alt={post.author.name}
                        className="w-16 h-16 rounded-full object-cover"
                      />
                    )}
                    <div>
                      <p className="text-white/40 text-xs uppercase tracking-wider mb-1">
                        {t('written_by', 'Scritto da')}
                      </p>
                      <h3 className="text-white text-lg font-medium">{post.author.name}</h3>
                      <p className="text-[#B08B5C] text-sm">{post.author.role}</p>
                      {post.author.bio_short && (
                        <p className="text-white/60 text-sm mt-3">{post.author.bio_short}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Share */}
              <div className="mt-8 pt-6 border-t border-white/10">
                <p className="text-white/40 text-sm mb-3">{t('share_article', 'Condividi l\'articolo')}</p>
                <div className="flex items-center gap-3">
                  <a 
                    href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full bg-white/5 hover:bg-[#1877F2]/20 hover:text-[#1877F2] flex items-center justify-center text-white/50 transition-all"
                    title="Condividi su Facebook"
                  >
                    <Facebook className="w-4 h-4" />
                  </a>
                  <a 
                    href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full bg-white/5 hover:bg-white/20 hover:text-white flex items-center justify-center text-white/50 transition-all"
                    title="Condividi su X"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                    </svg>
                  </a>
                  <a 
                    href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(shareTitle)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full bg-white/5 hover:bg-[#0A66C2]/20 hover:text-[#0A66C2] flex items-center justify-center text-white/50 transition-all"
                    title="Condividi su LinkedIn"
                  >
                    <Linkedin className="w-4 h-4" />
                  </a>
                  <button 
                    onClick={() => {
                      navigator.clipboard.writeText(shareUrl);
                      alert('Link copiato!');
                    }}
                    className="w-10 h-10 rounded-full bg-white/5 hover:bg-[#B08B5C]/20 hover:text-[#B08B5C] flex items-center justify-center text-white/50 transition-all"
                    title="Copia link"
                  >
                    <Share2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </article>

            {/* Sidebar */}
            <aside className="lg:w-80 shrink-0">
              <div className="sticky top-24 space-y-8">
                
                {/* Table of Contents */}
                {tableOfContents.length > 0 && (
                  <div className="bg-[#121212] rounded-2xl p-5">
                    <h3 className="text-white font-medium mb-4 flex items-center gap-2">
                      {t('toc_title', 'In questo articolo')}
                    </h3>
                    <nav className="space-y-2">
                      {tableOfContents.map((item, idx) => (
                        <button
                          key={idx}
                          onClick={() => scrollToSection(item.id)}
                          className={`block w-full text-left text-sm py-1.5 px-3 rounded-lg transition-colors ${
                            activeTocItem === item.id
                              ? 'bg-[#B08B5C]/20 text-[#B08B5C]'
                              : 'text-white/50 hover:text-white hover:bg-white/5'
                          }`}
                        >
                          {item.title}
                        </button>
                      ))}
                    </nav>
                  </div>
                )}

                {/* Materials */}
                {postMaterials.length > 0 && (
                  <div className="bg-[#121212] rounded-2xl p-5">
                    <h3 className="text-white font-medium mb-4">
                      {t('featured_materials_title', 'Materiali in evidenza')}
                    </h3>
                    <div className="space-y-3">
                      {postMaterials.map((material) => (
                        <Link
                          key={material.id}
                          to={`/materials/${material.id}`}
                          className="flex items-center gap-3 group"
                        >
                          {material.image_url && (
                            <img 
                              src={material.image_url} 
                              alt={material.name}
                              className="w-12 h-12 rounded-lg object-cover"
                            />
                          )}
                          <div>
                            <p className="text-white text-sm font-medium group-hover:text-[#B08B5C] transition-colors">
                              {material.name}
                            </p>
                            <p className="text-white/40 text-xs capitalize">{material.category}</p>
                          </div>
                        </Link>
                      ))}
                    </div>
                    
                    <Link 
                      to="/materials"
                      className="inline-flex items-center gap-1 text-[#B08B5C] text-sm mt-4 hover:underline"
                    >
                      {t('view_all_materials', 'Vedi tutti i materiali')}
                      <ChevronRight className="w-4 h-4" />
                    </Link>
                  </div>
                )}

                {/* CTA */}
                <div className="bg-gradient-to-br from-[#B08B5C]/20 to-transparent rounded-2xl p-5 border border-[#B08B5C]/20">
                  <h3 className="text-white font-medium mb-2">
                    {t('cta_title', 'Hai bisogno di aiuto?')}
                  </h3>
                  <p className="text-white/60 text-sm mb-4">
                    {t('cta_description', 'Contatta i nostri esperti per una consulenza personalizzata.')}
                  </p>
                  <Link
                    to="/contact"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-[#B08B5C] text-black text-sm font-medium rounded-lg hover:bg-[#9a7a50] transition-colors"
                  >
                    {t('cta_button', 'Contattaci')}
                    <ChevronRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </aside>
          </div>

          {/* Photo Gallery */}
          {gallery.length > 0 && (
            <section className="py-16 mt-12 border-t border-white/5">
              <h2 className="text-2xl font-light text-white mb-8">
                {t('gallery_title', 'Fotogallery')}
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {gallery.map((image, idx) => (
                  <div
                    key={image.id}
                    className="relative aspect-square rounded-xl overflow-hidden cursor-zoom-in group"
                    onClick={() => openLightbox(
                      gallery.map(g => ({
                        url: g.image_url,
                        alt: g.alt_text,
                        hotspots: g.hotspots || [],
                        caption: g.caption_it
                      })),
                      idx
                    )}
                  >
                    <img 
                      src={image.thumbnail_url || image.image_url}
                      alt={image.alt_text || ''}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    
                    {/* Overlay */}
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <ZoomIn className="w-8 h-8 text-white" />
                    </div>
                    
                    {/* Hotspot indicator */}
                    {image.hotspots?.length > 0 && (
                      <div className="absolute top-2 right-2 px-2 py-1 rounded-full bg-[#B08B5C]/80 text-black text-xs font-medium">
                        {image.hotspots.length} hotspot
                      </div>
                    )}
                    
                    {/* Caption */}
                    {image.caption_it && (
                      <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/80 to-transparent">
                        <p className="text-white text-sm truncate">{image.caption_it}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Related Articles */}
          {relatedPosts.length > 0 && (
            <section className="py-16 mt-12 border-t border-white/5">
              <h2 className="text-2xl font-light text-white mb-8">
                {t('related_articles', 'Articoli correlati')}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedPosts.map((relatedPost) => (
                  <Link
                    key={relatedPost.id}
                    to={`/inspiration/${relatedPost.slug}`}
                    className="group"
                  >
                    <div className="aspect-[4/3] rounded-xl overflow-hidden bg-[#121212] mb-4">
                      {relatedPost.cover_image_url && (
                        <img 
                          src={relatedPost.cover_image_url}
                          alt={relatedPost.title_it}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      )}
                    </div>
                    <h3 className="text-white font-medium group-hover:text-[#B08B5C] transition-colors line-clamp-2">
                      {relatedPost.title_it}
                    </h3>
                    <div className="flex items-center gap-2 text-white/40 text-sm mt-2">
                      <Clock className="w-3.5 h-3.5" />
                      {relatedPost.reading_time || 5} min
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </main>
      
      <Footer />

      {/* Lightbox */}
      <ImageLightbox
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        images={lightboxImages}
        initialIndex={lightboxIndex}
      />
    </div>
  );
};

export default InspirationDetailPage;
